import React, { useCallback, useEffect, useState } from 'react';
import { Button, Modal } from '@/components';
import { TableData, TableDataColumn } from '@/design-system/table-data';
import {
  ProviderClient,
  getProviderClients,
  createProviderClient,
  updateProviderClient,
  deleteProviderClient,
} from '@/services/api/providers';
import { toast } from '@/design-system/toaster';
import DeleteConfirmModal from './DeleteConfirmModal';

interface ProviderClientsTabProps {
  providerId: number | string;
}

export const ProviderClientsTab: React.FC<ProviderClientsTabProps> = ({
  providerId,
}) => {
  const [clients, setClients] = useState<ProviderClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Modals
  const [formOpen, setFormOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<ProviderClient | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ProviderClient | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Form state
  const [refCode, setRefCode] = useState('');
  const [name, setName] = useState('');
  const [label, setLabel] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [phone, setPhone] = useState('');

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getProviderClients(providerId, { search });
      setClients(res.data ?? []);
    } catch (err: any) {
      toast.error('Failed to load sub-clients');
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, [providerId, search]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const openCreateModal = () => {
    setEditingClient(null);
    setRefCode('');
    setName('');
    setLabel('');
    setAddress('');
    setCity('');
    setPhone('');
    setFormOpen(true);
  };

  const openEditModal = (client: ProviderClient) => {
    setEditingClient(client);
    setRefCode(client.referenceCode ?? '');
    setName(client.name);
    setLabel(client.label ?? '');
    setAddress(client.address);
    setCity(client.city ?? '');
    setPhone(client.phoneNumber ?? '');
    setFormOpen(true);
  };

  const handleSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
    e?.preventDefault();
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: Partial<ProviderClient> = {
        referenceCode: refCode.trim() || undefined,
        name: name.trim(),
        label: label.trim() || undefined,
        address: address.trim() || '',
        city: city.trim() || undefined,
        phoneNumber: phone.trim() || undefined,
      };

      if (editingClient) {
        await updateProviderClient(providerId, editingClient.id, payload);
        toast.success('Sub-client updated successfully');
      } else {
        await createProviderClient(providerId, payload);
        toast.success('Sub-client created successfully');
      }

      setFormOpen(false);
      fetchClients();
    } catch (err: any) {
      toast.error(err.message || 'Failed to save sub-client');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteProviderClient(providerId, deleteTarget.id);
      toast.success('Sub-client deleted');
      setDeleteTarget(null);
      fetchClients();
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete sub-client');
    } finally {
      setIsDeleting(false);
    }
  };

  const inputCls =
    'w-full rounded-gsm border border-stroke-soft-200 bg-white px-3 py-2 text-sm text-text-strong-950 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary';
  const labelCls =
    'mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600';

  const columns: TableDataColumn<ProviderClient>[] = [
    {
      key: 'index',
      title: 'No',
      width: '60px',
      render: (_v, _r, index) => (
        <span className="font-mono text-xs tabular text-text-sub-600">
          {index + 1}
        </span>
      ),
    },
    {
      key: 'referenceCode',
      title: 'Reference Code',
      render: (_v, record) => (
        <span className="font-mono text-xs font-semibold text-text-strong-950 tabular">
          {record.referenceCode || '—'}
        </span>
      ),
    },
    {
      key: 'name',
      title: 'Name',
      render: (_v, record) => (
        <div>
          <span className="font-medium text-text-strong-950">{record.name}</span>
          {record.label && (
            <p className="text-xs text-text-sub-600 font-mono">{record.label}</p>
          )}
        </div>
      ),
    },
    {
      key: 'address',
      title: 'Address',
      render: (_v, record) => (
        <span className="text-xs text-text-sub-600 truncate max-w-xs block" title={record.address}>
          {record.address || '—'}
        </span>
      ),
    },
    {
      key: 'city',
      title: 'City',
      render: (_v, record) => (
        <span className="text-xs text-text-sub-600">{record.city || '—'}</span>
      ),
    },
    {
      key: 'actions',
      title: 'Actions',
      width: '100px',
      align: 'right',
      render: (_v, record) => (
        <div className="inline-flex items-center gap-1">
          <Button
            variant="ghost"
            size="xs"
            onClick={(e) => {
              e?.stopPropagation();
              openEditModal(record);
            }}
            aria-label="Edit client"
          >
            <i className="ri-pencil-line text-[14px]" />
          </Button>
          <Button
            variant="ghost"
            size="xs"
            className="text-red-600 hover:text-red-700"
            onClick={(e) => {
              e?.stopPropagation();
              setDeleteTarget(record);
            }}
            aria-label="Delete client"
          >
            <i className="ri-delete-bin-line text-[14px]" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {/* Header bar */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 max-w-xs">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search sub-client name…"
            className={inputCls}
          />
        </div>
        <Button onClick={openCreateModal}>
          <i className="ri-add-line mr-1.5" />
          Add Sub-Client
        </Button>
      </div>

      {/* Sub-clients Table */}
      <div className="rounded-gsm border border-stroke-soft-200 bg-white">
        <TableData
          columns={columns}
          data={clients}
          loading={loading}
          emptyState={
            <div className="p-8 text-center text-text-sub-600">
              <i className="ri-user-shared-line text-3xl mb-2 text-text-sub-600/50" />
              <p className="text-sm font-medium">No sub-clients registered</p>
            </div>
          }
        />
      </div>

      {/* Add / Edit Sub-Client Modal */}
      <Modal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        icon="ri-user-shared-line"
        iconTone="primary"
        title={editingClient ? 'Edit Sub-Client' : 'Add Sub-Client'}
        subtitle="Manage merchant and endpoint references linked to this provider"
        width="md"
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="white"
              onClick={() => setFormOpen(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button onClick={() => handleSubmit()} disabled={isSubmitting}>
              {isSubmitting
                ? 'Saving…'
                : editingClient
                ? 'Save Changes'
                : 'Create Sub-Client'}
            </Button>
          </div>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Reference Code</label>
              <input
                type="text"
                value={refCode}
                onChange={(e) => setRefCode(e.target.value)}
                placeholder="e.g. CLI-001"
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Label / Tag</label>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Branch Pharmacy"
                className={inputCls}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Apotek Sehat"
                className={inputCls}
                required
              />
            </div>
            <div>
              <label className={labelCls}>Phone Number</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                placeholder="08123456789"
                className={inputCls}
              />
            </div>
          </div>

          <div>
            <label className={labelCls}>Address</label>
            <textarea
              rows={2}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Delivery / pickup address"
              className={inputCls}
            />
          </div>

          <div>
            <label className={labelCls}>City</label>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="e.g. Jakarta Pusat"
              className={inputCls}
            />
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Sub-Client?"
        subtitle={deleteTarget?.name}
        description="Are you sure you want to delete this sub-client? Linked destination routing may be affected."
        confirmText="Yes, Delete"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default ProviderClientsTab;
